import { useQuery } from "@tanstack/react-query";
import { fetchNetworkDefCompDetails } from "../../../../utils/apiUtils";
import { toIsoDate } from "../../../finance/financeShared";
import NetworkDefinition from "./NetworkDefinition";

const isCurrentVersion = (row) => {
    const exp = row?.versionExpDateKey;
    if (exp == null) return true;
    if (String(exp) === "9223372036854776000") return true;
    return Number(exp) > Date.now();
};

const pickCurrentDefs = (rows) => {
    const byKey = new Map();
    for (const row of rows || []) {
        const key = row.networkDefCompKey ?? row.networkDefCompHistFactKey;
        if (key == null) continue;
        const prev = byKey.get(key);
        if (!prev) {
            byKey.set(key, row);
            continue;
        }
        const rowCurrent = isCurrentVersion(row);
        const prevCurrent = isCurrentVersion(prev);
        if (rowCurrent && !prevCurrent) {
            byKey.set(key, row);
        } else if (rowCurrent === prevCurrent) {
            const rowHist = Number(row.networkDefCompHistFactKey) || 0;
            const prevHist = Number(prev.networkDefCompHistFactKey) || 0;
            if (rowHist > prevHist) byKey.set(key, row);
        }
    }
    return [...byKey.values()];
};

const NetworkDefCard = ({ def, asOf }) => {
    const lookup = def.networkDefCompKey != null
        ? { networkDefCompKey: def.networkDefCompKey }
        : def.networkDefCompHistFactKey != null
            ? { networkDefCompHistFactKey: def.networkDefCompHistFactKey }
            : def.networkDefCompName
                ? { networkDefCompName: def.networkDefCompName }
                : null;

    const year = Number(String(asOf || "").slice(0, 4));
    const asOfDate = !asOf || year <= 1800 || year >= 2999 ? toIsoDate(new Date()) : asOf;

    const { data, isLoading, error } = useQuery({
        queryKey: ["network-def-comp-details", lookup, asOfDate],
        queryFn: () => fetchNetworkDefCompDetails({ ...lookup, asOfDate }),
        enabled: !!lookup,
        staleTime: 60_000,
        retry: false,
    });

    const details = data && !data.errorMessage ? data : null;

    return (
        <div
            style={{
                backgroundColor: "var(--white)",
                borderRadius: ".6rem",
                boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1px",
                padding: "1rem 1.2rem",
                marginTop: "1rem",
            }}
        >
            {isLoading && <p className="sub-title">Loading network definition…</p>}
            {(error || data?.errorMessage) && !isLoading && (
                <p className="danger">{error?.message || data.errorMessage}</p>
            )}
            {!isLoading && <NetworkDefinition data={{ ...def, details }} />}
        </div>
    );
};

const PlanNetworksSection = ({ networkDefinitions, asOf }) => {
    const defs = pickCurrentDefs(networkDefinitions);

    return (
        <div style={{ paddingTop: "1rem" }}>
            <p style={{ fontSize: "1.2rem", fontWeight: 600 }}>Network Definition</p>
            <p className="sub-title" style={{ paddingTop: ".3rem" }}>
                In-network tiers and benefit networks for this plan
            </p>
            {!defs.length && (
                <p className="sub-title" style={{ paddingTop: "1rem" }}>
                    No Network Definition is linked to this plan.
                </p>
            )}
            {defs.map((def) => (
                <NetworkDefCard
                    key={def.networkDefCompKey || def.networkDefCompHistFactKey}
                    def={def}
                    asOf={asOf}
                />
            ))}
        </div>
    );
};

export default PlanNetworksSection;
